/**
 * API client.
 *
 * Hand-rolled over fetch rather than a data-fetching library: the whole app has
 * a handful of endpoints, and spec §7 budgets under 150KB of JS on first load.
 * A cache layer we would use twice is not worth 12KB on a 3G connection.
 *
 * The access token lives in memory; the refresh token in localStorage. A cheap
 * Android kills background tabs aggressively, so a session that only survived
 * in memory would log people out constantly — but keeping the short-lived
 * access token out of storage still limits what an XSS bug could walk away
 * with to a 30-minute window.
 */

const BASE = import.meta.env['VITE_API_BASE'] ?? '';
const REFRESH_KEY = 'bongo.refresh';

export interface RoundSummary {
  readonly roundId: string;
  readonly tierId: string;
  readonly roundNumber: number;
  readonly state: string;
  readonly legs: number;
  readonly entryCutoffAt: string;
  readonly prizePoolSenti: string;
  readonly ticketCount: number;
  readonly isProgressive: true;
}

export interface Fixture {
  readonly id: string;
  readonly home: { readonly name: string };
  readonly away: { readonly name: string };
  readonly kickoffAt: string;
}

export interface LadderRung {
  readonly correctLegs: number;
  readonly missedLegs: number;
  readonly allocationSenti: string;
}

export interface RoundDetail {
  readonly roundKey: string;
  readonly tierId: string;
  readonly legs: number;
  readonly state: string;
  readonly baseUnitSenti: string;
  readonly allowedMultipliers: readonly number[];
  readonly fixtures: readonly Fixture[];
  readonly entryCutoffAt: string;
  readonly secondsRemaining: number;
  readonly prizePoolSenti: string;
  readonly rolloverInSenti: string;
  readonly isProgressive: true;
  readonly ticketCount: number;
  readonly crowdPicks: readonly { home: number; draw: number; away: number }[];
  readonly ladder: readonly LadderRung[];
}

export interface Session {
  readonly userId: string;
  readonly phone: string;
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
}

export interface Wallet {
  readonly balanceSenti: string;
  readonly spentThisWeekSenti: string;
  readonly weeklyCapSenti: string;
  readonly remainingCapSenti: string;
  readonly selfExcludedUntil: string | null;
}

export interface WalletTransaction {
  readonly occurredAt: string;
  readonly kind: string;
  readonly amountSenti: string;
  readonly metadata: Record<string, string>;
}

export interface TicketDetail {
  readonly ticketId: string;
  readonly roundId: string;
  readonly picks: readonly string[];
  readonly units: number;
  readonly stakeSenti: string;
  readonly purchasedAt: string;
  readonly progress: { correctLegs: number; missedLegs: number } | null;
  readonly grossPayoutSenti: string | null;
  readonly settledAt: string | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;

export function loadRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    // Private browsing can throw on storage access. Not fatal — the user just
    // signs in again next visit.
    return null;
  }
}

export function storeSession(session: Session | null): void {
  accessToken = session?.accessToken ?? null;
  try {
    if (session === null) localStorage.removeItem(REFRESH_KEY);
    else localStorage.setItem(REFRESH_KEY, session.refreshToken);
  } catch {
    /* storage unavailable; session survives for this tab only */
  }
}

export function isSignedIn(): boolean {
  return accessToken !== null;
}

async function request<T>(path: string, init: RequestInit = {}, retryOn401 = true): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  if (accessToken !== null) headers.set('authorization', `Bearer ${accessToken}`);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const text = await res.text();
  const body: unknown = text.length > 0 ? JSON.parse(text) : {};

  // One transparent refresh attempt. Access tokens are 30 minutes, so an app
  // left open across that boundary should not bounce the user to a login form.
  if (res.status === 401 && retryOn401) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(path, init, false);
  }

  if (!res.ok) {
    const err =
      typeof body === 'object' && body !== null && 'error' in body
        ? (body.error as { code?: string; message?: string })
        : {};
    throw new ApiError(res.status, err.code ?? 'unknown', err.message ?? res.statusText);
  }
  return body as T;
}

async function tryRefresh(): Promise<boolean> {
  const refreshToken = loadRefreshToken();
  if (refreshToken === null) return false;
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      storeSession(null);
      return false;
    }
    const session = (await res.json()) as Session;
    storeSession(session);
    return true;
  } catch {
    return false;
  }
}

export const api = {
  // --- auth ---------------------------------------------------------------
  register: (input: {
    phone: string;
    password: string;
    weeklyCapSenti?: string;
  }): Promise<Session> =>
    request('/auth/register', { method: 'POST', body: JSON.stringify(input) }),

  login: (input: { phone: string; password: string }): Promise<Session> =>
    request('/auth/login', { method: 'POST', body: JSON.stringify(input) }),

  logout: async (): Promise<void> => {
    const refreshToken = loadRefreshToken();
    storeSession(null);
    if (refreshToken !== null) {
      await fetch(`${BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => undefined);
    }
  },

  restore: tryRefresh,

  me: (): Promise<{ userId: string; phone: string; selfExcludedUntil: string | null }> =>
    request('/auth/me'),

  // --- rounds -------------------------------------------------------------
  listRounds: (): Promise<{ rounds: readonly RoundSummary[] }> => request('/rounds'),
  getRound: (id: string): Promise<RoundDetail> => request(`/rounds/${encodeURIComponent(id)}`),

  // --- tickets ------------------------------------------------------------
  buyTicket: (roundId: string, picks: readonly string[], multiplier: number) =>
    request<{ ticketId: string; stakeSenti: string; balanceAfterSenti: string }>('/tickets', {
      method: 'POST',
      body: JSON.stringify({ roundId, picks, multiplier }),
    }),

  quickPick: (roundId: string): Promise<{ picks: readonly string[] }> =>
    request('/tickets/quick-pick', { method: 'POST', body: JSON.stringify({ roundId }) }),

  getTicket: (ticketId: string, roundId: string): Promise<TicketDetail> =>
    request(`/tickets/${encodeURIComponent(ticketId)}?roundId=${encodeURIComponent(roundId)}`),

  // --- wallet -------------------------------------------------------------
  wallet: (): Promise<Wallet> => request('/wallet'),

  transactions: (): Promise<{ transactions: readonly WalletTransaction[] }> =>
    request('/wallet/transactions'),

  deposit: (amountSenti: string): Promise<{ intentId: string; message: string }> =>
    request('/wallet/deposit', { method: 'POST', body: JSON.stringify({ amountSenti }) }),

  withdraw: (amountSenti: string): Promise<{ intentId: string; status: string }> =>
    request('/wallet/withdraw', { method: 'POST', body: JSON.stringify({ amountSenti }) }),

  // --- responsible gambling ------------------------------------------------
  setWeeklyCap: (weeklyCapSenti: string) =>
    request<{ effective: string; message?: string }>('/rg/limits', {
      method: 'PUT',
      body: JSON.stringify({ weeklyCapSenti }),
    }),

  selfExclude: (durationDays: number) =>
    request<{ selfExcludedUntil: string }>('/rg/self-exclude', {
      method: 'POST',
      body: JSON.stringify({ durationDays }),
    }),
};
